import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getProductDetail, getShipping } from './aliapi';
import {
  Container,
  Row,
  Col,
  Button,
  Spinner,
  Carousel,
  Card,
  Badge
} from 'react-bootstrap';
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";

const countryMap = {
  CN: 'China',
  US: 'United States',
  NG: 'Nigeria',
};

const formatPrice = (price) => {
  if (!price) return 'N/A';
  const numeric = parseFloat(price);
  return numeric.toLocaleString('en-NG', { style: 'currency', currency: 'NGN' });
};

const AliProductDetail = () => {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [shippingOptions, setShippingOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [carouselIndex, setCarouselIndex] = useState(0);

  useEffect(() => {
    const load = async () => {
      const data = await getProductDetail(productId);
      const productData = data?.full?.aliexpress_ds_product_get_response?.result;
      setProduct(productData);

      const sku = productData?.ae_item_sku_info_dtos?.ae_item_sku_info_d_t_o?.[0]?.sku_id;
      if (sku) {
        const ship = await getShipping(productId, sku, 'NG');
        const options = ship?.aliexpress_ds_freight_query_response?.result?.delivery_options?.delivery_option_d_t_o || [];
        setShippingOptions(options);
      }

      setLoading(false);
    };
    load();
  }, [productId]);

  useEffect(() => {
    if (!product) return;
    const imgs = document.querySelectorAll('.description-content img');
    imgs.forEach((img) => {
      img.style.maxWidth = '100%';
      img.style.height = 'auto';
      img.style.display = 'block';
      img.style.margin = '10px auto';
    });
  }, [product]);

  const handleCopyURL = () => {
    navigator.clipboard.writeText(window.location.href);
    alert('Product URL copied!');
  };

  if (loading || !product) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
      </Container>
    );
  }

  const images = product?.ae_multimedia_info_dto?.image_urls?.split(';').filter(Boolean) || [];
  const detailHTML = product?.ae_item_base_info_dto?.detail || '';
  const price = product?.ae_item_sku_info_dtos?.ae_item_sku_info_d_t_o?.[0]?.offer_sale_price || 'N/A';
  const currencycode = product?.ae_item_sku_info_dtos?.ae_item_sku_info_d_t_o?.[0]?.currency_code;
  const skuavailable = product?.ae_item_sku_info_dtos?.ae_item_sku_info_d_t_o?.[0]?.sku_available_stock;
  const title = product?.ae_item_base_info_dto?.subject || 'Untitled Product';
  const store = product?.ae_store_info?.store_name || 'Unknown Store';
  const formattedPrice = formatPrice(price);

  const handleWhatsApp = () => {
    const productUrl = window.location.href;
    const deliveryOptionsText = shippingOptions
      .map((option) => `• ${option.company}: ${option.shipping_fee_format}`)
      .join('\n');
  
    const message = `
  Hello! I'm interested in this product:
  
  🛒 *${title}*
  💵 *Price:* ₦${formattedPrice} ${currencycode}
  🚚 *Delivery Options:*
  ${deliveryOptionsText}
  
  🔗 *Product Link:* ${productUrl}
    `.trim();
  
    const encodedMessage = encodeURIComponent(message);
    const whatsappNumber = '2349098370120'; // ✅ your requested number
    window.open(`https://wa.me/${whatsappNumber}?text=${encodedMessage}`, '_blank');
  };
  

  return (
    <>
      <Navbar />
      <Container className="py-5">
        <Card className="shadow border-0">
          <Row className="g-0">
            <Col md={6}>
              <div className="position-relative p-3">
                <img
                  src="https://ae01.alicdn.com/kf/Sa0202ec8a96a4085962acfc27e9ffd04F/1080x1080.jpg"
                  alt="AliExpress"
                  style={{
                    position: 'absolute',
                    top: '10px',
                    left: '10px',
                    width: '80px',
                    borderRadius: '50%',
                    zIndex: 2,
               
                    backgroundColor: '#fff',
                    padding: '3px',
                  }}
                />
                <Carousel
                  activeIndex={carouselIndex}
                  onSelect={(selectedIndex) => setCarouselIndex(selectedIndex)}
                  controls
                  indicators={false}
                  interval={null}
                  className="shadow-sm rounded overflow-hidden"
                >
                  {images.map((img, idx) => (
                    <Carousel.Item key={idx}>
                      <img
                        src={img}
                        alt={`Image ${idx + 1}`}
                        className="d-block w-100"
                        style={{ objectFit: 'contain', maxHeight: '400px', backgroundColor: '#f8f9fa' }}
                      />
                    </Carousel.Item>
                  ))}
                </Carousel>

                <div className="d-flex justify-content-center mt-2 gap-2 flex-wrap">
                  {images.map((_, idx) => (
                    <Button
                      key={idx}
                      variant={idx === carouselIndex ? 'primary' : 'outline-secondary'}
                      size="sm"
                      className="rounded-circle"
                      style={{ width: '36px', height: '36px' }}
                      onClick={() => setCarouselIndex(idx)}
                    >
                      {idx + 1}
                    </Button>
                  ))}
                </div>
              </div>
            </Col>

            <Col md={6}>
              <Card.Body className="p-4">
                <h4 className="fw-bold">{title}</h4>
                <h5 className="text-success my-2">
                  {formattedPrice}{' '} <Badge bg="light" text="dark">{currencycode}</Badge>
                </h5>
                <p className="text-muted mb-3">
                  <strong>Store:</strong> {store}<br/>
                  <Badge bg="dark" text="light">SKU Available Stock: {skuavailable}</Badge>
                </p>

                {shippingOptions.length > 0 && (
                  <div className="bg-light p-3 rounded border mb-3">
                    <h6 className="fw-bold mb-3">Shipping Options to Nigeria</h6>
                    <Row>
                      {shippingOptions.map((option, idx) => (
                        <Col key={idx} xs={12} md={6} className="mb-3">
                          <div className="border rounded p-3 h-100 bg-white shadow-sm">
                            <p className="mb-1"><strong>Carrier:</strong> {option.company}</p>
                            <p className="mb-1"><strong>Delivery Fee:</strong> {option.shipping_fee_format}</p>
                            <p className="mb-1"><strong>Delivery:</strong> {option.delivery_date_desc}</p>
                            <p className="mb-1"><strong>Max Delivery:</strong> {option.max_delivery_days} days</p>
                            <p className="mb-1"><strong>Min Delivery:</strong> {option.min_delivery_days} days</p>
                            <p className="mb-1"><strong>From:</strong> {countryMap[option.ship_from_country] || option.ship_from_country}</p>
                            {option.free_shipping_threshold && (
                              <p className="mb-1"><strong>Free Shipping Over:</strong> {option.free_shipping_threshold}</p>
                            )}
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </div>
                )}

                <div className="d-flex gap-2 mt-3 flex-wrap">
                  <Button variant="success" onClick={handleWhatsApp}>Buy on WhatsApp</Button>
                  <Button variant="outline-dark" onClick={handleCopyURL}>Copy Product URL</Button>
                </div>
              </Card.Body>
            </Col>
          </Row>
        </Card>

        {detailHTML && (
          <Card className="shadow-sm border-0 mt-4">
            <Card.Body className="p-4">
              <h5 className="fw-bold mb-3">Product Description</h5>
              <div
                className="bg-white p-3 rounded description-content"
                style={{ maxWidth: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch', border: '1px solid #eaeaea' }}
              >
                <div
                  dangerouslySetInnerHTML={{ __html: detailHTML }}
                  style={{ minWidth: '100%', wordWrap: 'break-word', fontSize: '0.95rem', lineHeight: '1.6' }}
                />
              </div>
            </Card.Body>
          </Card>
        )}
      </Container>
      <Footer />
    </>
  );
};

export default AliProductDetail;